<template>
  <div class="custom-tree-node">
    <div class="node-content" style="display: flex; align-items: center;">
      <!-- Expand/Collapse Icon -->
      <!-- Render the icon container always, but hide content if no children to preserve alignment if we wanted, 
           but here we use visibility: hidden to keep space. 
           NOTE: If allow-aligned without children, removing visibility: hidden would align text. 
           But usually we want arrows only for parents. -->
      <div 
        class="toggle-icon2 user-select-none2" 
        @click.stop="toggleExpand" 
        :style="{ visibility: hasChildren ? 'visible' : 'hidden' }"
      >
        <i :class="expandedIconClass"></i>
      </div>

      <!-- Checkbox -->
      <div class="custom-control custom-checkbox d-inline-block" style="margin-left: 4px;">
        <input 
          type="checkbox" 
          class="form-check-input me-1" 
          :id="`checkbox-${node.id}`"
          :checked="isChecked"
          :disabled="isDisabled"
          @change="onCheckChange"
          :indeterminate="isIndeterminate"
        >
        <label class="form-check-label" :for="`checkbox-${node.id}`" style="user-select: none; cursor: pointer;">
          {{ node[fields.text] }}
        </label>
      </div>
    </div>

    <!-- Children -->
    <div v-show="isExpanded && hasChildren" class="node-children" style="padding-left: 28px;">
      <CustomTreeNode
        ref="childrenRefs"
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :fields="fields"
        :selectedNodes="selectedNodes"
        :disabledNodes="disabledNodes"
        @check-change="$emit('check-change', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import './CustomTreeNode.css';
import { defineComponent, toRef, ref, computed } from 'vue';

export default defineComponent({
  name: 'CustomTreeNode',
  props: {
    node: {
      type: Object,
      required: true
    },
    selectedNodes: {
      type: Array,
      default: () => []
    },
    disabledNodes: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Object,
      default: () => ({ id: 'id', text: 'text', children: 'children' })
    }
  },
  emits: ['check-change'],
  setup(props, { emit }) {
    const isExpanded = ref(false);

    const hasChildren = computed(() => {
      return props.node.children && props.node.children.length > 0;
    });

    const isChecked = computed(() => {
      return props.selectedNodes.includes(props.node.id);
    });

    const isIndeterminate = computed(() => {
      if (isChecked.value) return false;
      if (!hasChildren.value) return false;
      
      const childIds = getAllDescendants(props.node);
      const checkedChildren = childIds.filter((id: any) => props.selectedNodes.includes(id));
      
      return checkedChildren.length > 0 && checkedChildren.length < childIds.length;
    });
    
    const getAllDescendants = (node: any): any[] => {
      let ids: any[] = [];
      if (node.children) {
        node.children.forEach((child: any) => {
          ids.push(child.id);
          ids = ids.concat(getAllDescendants(child));
        });
      }
      return ids;
    };

    const isDisabled = computed(() => {
      return props.disabledNodes.includes(props.node.id);
    });

    const toggleExpand = () => {
      if (hasChildren.value) {
        isExpanded.value = !isExpanded.value;
      }
    };

    const onCheckChange = (event: any) => {
      emit('check-change', { node: props.node, checked: event.target.checked });
    };

    const expandedIconClass = computed(() => {
        return isExpanded.value ? 'fas fa-angle-down' : 'fas fa-angle-right';
    });
    
    // Refs for recursion
    const childrenRefs = ref<any[]>([]);

    const setExpanded = (expanded: boolean) => {
        isExpanded.value = expanded;
        // Recursively set children
        // Use nextTick to ensure children are mounted if expanding? 
        // If we are expanding, children might not be rendered yet if using v-if.
        // But we use v-show for children container (actually v-show="isExpanded && hasChildren").
        // Wait, template has v-show="isExpanded && hasChildren".
        // SO children components ARE mounted? 
        // NO, v-for is inside the v-show div. 
        // If v-show is false, they are mounted but hidden? Yes v-show toggles display:none.
        // So they should be mounted.
        
        // Wait, line 35 uses v-show.
        // However, if I change isExpanded to true, v-show becomes true.
        
        if (childrenRefs.value) {
            childrenRefs.value.forEach((child: any) => {
                if (child.setExpanded) {
                    child.setExpanded(expanded);
                }
            });
        }
    };

    return {
      isExpanded,
      hasChildren,
      isChecked,
      isDisabled,
      toggleExpand,
      onCheckChange,
      isIndeterminate,
      expandedIconClass,
      childrenRefs,
      setExpanded
    };
  }
});
</script>
